(() => {
  if (window.__orderSampleLoaded) return;
  window.__orderSampleLoaded = true;

/**
 * Order a Sample - Web Component
 * Renders on product cards for murals/wallcoverings. Opens a small popover with
 * the four material options; picking one adds an A4-sized sample of that
 * material to the cart, priced with the same £0.01-variant × quantity mechanism
 * as the mural area selector.
 */
class OrderSample extends HTMLElement {
  // Keep in sync with MATERIAL_RATES in mural-area-selector.js (per-m² rates).
  static MATERIAL_RATES = [
    { match: 'fabric-backed contract', rate: 54, label: 'Fabric-Backed Contract' },
    { match: 'fabric backed contract', rate: 54, label: 'Fabric-Backed Contract' },
    { match: 'fabric-backed (contract)', rate: 54, label: 'Fabric-Backed Contract' },
    { match: 'fabric', rate: 54, label: 'Fabric-Backed Contract' },
    { match: 'concrete', rate: 44, label: 'Concrete Texture' },
    { match: 'linen', rate: 44, label: 'Linen Texture' },
    { match: 'non-woven', rate: 39, label: 'Non-Woven' },
    { match: 'non woven', rate: 39, label: 'Non-Woven' },
  ];

  // A4 sheet: 0.21 m × 0.297 m
  static A4_AREA_M2 = 0.21 * 0.297;

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-sample-toggle]');
    this.samplePanel = this.querySelector('[data-sample-popover]');
    this.moneyFormat = this.dataset.moneyFormat || '£{{amount}}';
    this.productTitle = this.dataset.productTitle || '';

    if (!this.toggleBtn || !this.samplePanel) return;

    this.renderOptions();

    this.toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.togglePopover();
    });

    // Close when clicking outside
    this.onDocClick = (e) => {
      if (!this.contains(e.target)) this.closePopover();
    };
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
  }

  formatMoney(amount) {
    const fmt = this.moneyFormat;
    const fixed = amount.toFixed(2);
    const noDec = Math.round(amount).toString();
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, fixed)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, fixed.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  getVariants() {
    const dataEl = this.querySelector('[data-sample-variants]');
    if (!dataEl) return [];
    try { return JSON.parse(dataEl.textContent); } catch (e) { return []; }
  }

  matchRate(title) {
    const t = (title || '').toLowerCase();
    return OrderSample.MATERIAL_RATES.find((m) => t.includes(m.match)) || null;
  }

  renderOptions() {
    const list = this.samplePanel.querySelector('[data-sample-options]');
    if (!list) return;
    list.innerHTML = '';

    this.getVariants().forEach((v) => {
      const mat = this.matchRate(v.title);
      const realPrice = typeof v.price === 'number' ? v.price : parseFloat(v.price);
      const fixedPriced = realPrice > 0.02; // dedicated sample product / real variant price
      if (!fixedPriced && !mat) return;

      const label = fixedPriced ? v.title : (mat ? mat.label : v.title);
      const price = fixedPriced
        ? realPrice
        : Math.max(0.01, Math.round(mat.rate * OrderSample.A4_AREA_M2 * 100) / 100);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'order-sample__option';
      btn.innerHTML = `<span>${label}</span><span class="order-sample__option-price">${this.formatMoney(price)}</span>`;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addSample(v, { label, rate: mat ? mat.rate : realPrice, fixedPriced, price }, btn);
      });
      list.appendChild(btn);
    });
  }

  togglePopover() {
    this.samplePanel.hidden ? this.openPopover() : this.closePopover();
  }

  openPopover() {
    this.samplePanel.hidden = false;
    this.toggleBtn.setAttribute('aria-expanded', 'true');
  }

  closePopover() {
    this.samplePanel.hidden = true;
    this.toggleBtn.setAttribute('aria-expanded', 'false');
  }

  async addSample(variant, mat, btn) {
    // Dedicated sample product: qty 1 at its real price. £0.01 variants: pence-as-quantity trick.
    const qty = mat.fixedPriced ? 1 : Math.max(1, Math.round(mat.rate * OrderSample.A4_AREA_M2 * 100));
    const total = this.formatMoney(mat.fixedPriced ? mat.price : qty / 100);

    const properties = {};

if (mat.fixedPriced) {
  if (this.productTitle) properties['Design'] = this.productTitle;
} else {
  properties['Sample'] = 'A4 sample (21 × 29.7 cm)';
  properties['Material'] = mat.label;
  properties['Area'] = 'A4 sample';
  properties['_Calculated Total'] = total;

  if (this.productTitle) properties['Design'] = this.productTitle;
}

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
  const response = await fetch((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      items: [{
        id: variant.id,
        quantity: qty,
        properties: properties
      }]
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    alert(errorText);
    throw new Error(errorText);
  }

      this.closePopover();

      // Update cart badges everywhere, then re-render the mini cart and open it.
      try {
        const cartJson = await (await fetch('/cart.js')).json();
        if (window.theme && theme.pubsub) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'order-sample', cart: cartJson });
        }
      } catch (e) { /* badge update is best-effort */ }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    } catch (err) {
      console.error('[order-sample]', err);
      window.location.href = '/cart';
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }
}

if (!customElements.get('order-sample')) {
  customElements.define('order-sample', OrderSample);
}

})();
